import type { BoardLayout, BoardSortMode, Lane, PublicResultsLayout, PublicThemeMode } from './domain';

export interface ResultAttempt {
  lane: Lane;
  timerSlot: number;
  timeMs: number | null;
  isNp: boolean;
  isElectronic: boolean;
}

export type PenaltyCategory = 'OBSTACLES' | 'HOSE' | 'COUPLING' | 'EQUIPMENT' | 'OTHER' | 'CUSTOM';

export const PENALTY_CATEGORY_LABELS: Record<PenaltyCategory, string> = {
  OBSTACLES: 'Překážky',
  HOSE: 'Hadice',
  COUPLING: 'Spojka',
  EQUIPMENT: 'Nářadí',
  OTHER: 'Ostatní',
  CUSTOM: 'Vlastní',
};

export interface PenaltyCatalogItem {
  id: number;
  competitionId: number;
  disciplineId: number | null;
  code: string;
  label: string;
  seconds: number;
  category: PenaltyCategory;
  sortOrder: number;
  isActive: boolean;
}

/** Per-vyskyt zaznam trestneho v ramci jednoho Result (DETAILED mode). */
export interface ResultPenalty {
  id?: number;
  catalogId: number | null;
  code: string | null;
  label: string;
  seconds: number;
  count: number;
  lane: Lane | null;
  sortOrder: number;
}

export interface Result {
  id: number;
  teamId: number;
  teamName: string;
  teamStartNumber: number;
  disciplineId: number;
  disciplineName: string;
  attemptNumber: number;
  timeFinalMs: number | null;
  timeFinalFormatted: string;
  isNp: boolean;
  penaltySeconds: number;
  /** Pro kterou drahu plati trestne (pred agregaci); null = aplikovat na celkovy cas. */
  penaltyLane: Lane | null;
  rank: number | null;
  note: string | null;
  attempts: ResultAttempt[];
  /** DETAILED penalty zaznamy (per-vyskyt). Prazdne v SIMPLE/NONE rezimu. */
  penalties?: ResultPenalty[];
  createdAt: string;
  updatedAt: string;
  /** Cas ukonceni pokusu (Smernice dorostu 2022 kap. 5) — pro lhutu protestu. */
  completedAt?: string | null;
}

export interface SaveResultRequest {
  teamId: number;
  disciplineId: number;
  attemptNumber: number;
  isNp: boolean;
  penaltySeconds: number;
  /** Pro kterou drahu plati trestne; null/undefined = aplikovat na celkovy cas (puvodni chovani). */
  penaltyLane?: Lane | null;
  note?: string;
  attempts: ResultAttempt[];
  /** DETAILED penalty zaznamy z katalogu (pouze pri penalty_tracking_mode=DETAILED). */
  penalties?: ResultPenalty[];
}

export interface OverallRanking {
  categoryId: number;
  categoryName: string;
  teams: TeamRanking[];
}

export interface TeamRanking {
  teamId: number;
  teamName: string;
  startNumber: number;
  totalPoints: number;
  finalRank: number;
  perDisciplineRanks: Record<number, number>;
}

export interface CompetitionPublic {
  shortCode: string;
  name: string;
  location: string | null;
  competitionDate: string;
  status: 'DRAFT' | 'LIVE' | 'FINISHED';
  overallRankingVisible: boolean;

  // Konfigurace verejneho zobrazeni
  publicResultsLayout: PublicResultsLayout;
  publicShowStartNumber: boolean;
  publicShowPenaltySeconds: boolean;
  publicShowPoints: boolean;
  publicShowAttemptDetails: boolean;
  publicShowLaneDetails: boolean;
  publicShowNote: boolean;
  publicRotationSeconds: number;
  publicRotateOverall: boolean;
  publicAutoRefreshSeconds: number;
  publicThemeMode: PublicThemeMode;
  publicShowLogo: boolean;
  publicShowOrganizerLogo: boolean;
  publicOrganizerLogoUrl: string | null;
  publicOrganizerLogoMime: string | null;
  /** Skalovani velikosti organizer loga v procentech (50-200, default 100). */
  publicOrganizerLogoScale: number;

  boardLayout: BoardLayout;
  boardThemeMode: PublicThemeMode;
  /** Skalovani pisma boardu v procentech (50-200, default 100). */
  boardFontScale: number;
  /** Zobrazit vsechna druzstva (i bez vysledku) na boardu? */
  boardShowAllTeams: boolean;
  /** Razeni na boardu: RANK / START_NUMBER. */
  boardSortMode: BoardSortMode;
  /** Zobrazit casy jednotlivych drah (LP/PP, Draha 1/2) pod urednim casem? */
  boardShowLaneDetails: boolean;
  /** Zobrazit sporic na boardu dokud je soutez DRAFT? */
  boardScreensaverEnabled: boolean;
  /** Volitelny text na sporici (multiline). */
  boardCustomText: string | null;
  /** Volitelny disclaimer ve footeru verejne stranky. */
  publicDisclaimerText: string | null;

  /** ISO datetime planovaneho zverejneni (jen pokud je nyni pred publikaci). */
  publishAt: string | null;

  unitName: string | null;
  unitSlug: string | null;
  categories: import('./domain').Category[];
}
